import React from 'react';
import LoadingSpinner from '../../../../shared/components/LoadingSpinner';
import { useReferentDefinitions } from '../hooks/useReferentDefinitions';

const TYPE_KEY_MAX = 100;

const inputCls = 'w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60';
const labelCls = 'block text-xs font-medium text-cockpit-text-2 mb-1.5';

const ReferentDefinitionsContent: React.FC = () => {
  const {
    types,
    definitions,
    loading,
    error,
    saving,
    newTypeKey, setNewTypeKey,
    newTypeName, setNewTypeName,
    newTypeDesc, setNewTypeDesc,
    newKey, setNewKey,
    newName, setNewName,
    newDesc, setNewDesc,
    newFunctionTypeId, setNewFunctionTypeId,
    editingType, setEditingType,
    editTypeKey, setEditTypeKey,
    editTypeName, setEditTypeName,
    editTypeDesc, setEditTypeDesc,
    editTypeActive, setEditTypeActive,
    editing, setEditing,
    editName, setEditName,
    editDesc, setEditDesc,
    editFunctionTypeId, setEditFunctionTypeId,
    editActive, setEditActive,
    handleCreateType,
    openEditType,
    handleSaveEditType,
    handleDeactivateType,
    handleCreate,
    openEdit,
    handleSaveEdit,
    handleDeactivate,
  } = useReferentDefinitions();

  const activeTypes = types.filter((t) => t.is_active);

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-200">
        <p className="font-semibold text-cockpit-text mb-1">À quoi sert cet onglet ?</p>
        <p>
          Créez d'abord un <strong>type</strong> de référent, puis les <strong>fonctions</strong> associées à ce type.
          Chaque fonction appartient à un type : cela sert à organiser les rôles et les listes lors des assignations.
          Pour donner une fonction à un membre : onglet <strong>Gérer les rôles</strong>, puis l'icône référent.
        </p>
        <p className="mt-2 text-cockpit-text-2 text-xs">
          En cas de message d'erreur ou de fonctionnalité indisponible, contactez l'équipe technique du site.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</div>
      )}

      {/* Types */}
      <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 p-6 backdrop-blur-sm">
        <h3 className="text-base font-bold text-cockpit-text mb-4">Types référent</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-[#1a2537]">
          <div>
            <label className={labelCls}>Clé unique du type</label>
            <input
              value={newTypeKey}
              onChange={(e) => setNewTypeKey(e.target.value.slice(0, TYPE_KEY_MAX))}
              placeholder="ex. CM, Formation"
              maxLength={TYPE_KEY_MAX}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Nom affiché</label>
            <input
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="ex. Community Manager"
              className={inputCls}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Description (optionnel)</label>
            <textarea
              value={newTypeDesc}
              onChange={(e) => setNewTypeDesc(e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreateType}
          disabled={saving || !newTypeKey.trim() || !newTypeName.trim()}
          className="mb-6 rounded-lg border border-emerald-500/40 bg-emerald-500/20 px-4 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Enregistrement…' : 'Créer le type'}
        </button>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : types.length === 0 ? (
          <p className="text-cockpit-text-2 text-sm">Aucun type. Créez-en un ci-dessus (ou importez le schéma SQL).</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1a2537] text-left">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Clé</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Statut</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-cockpit-text">{t.type_key}</td>
                    <td className="px-4 py-3 text-cockpit-text">{t.name}</td>
                    <td className="px-4 py-3">
                      {t.is_active
                        ? <span className="rounded px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">Actif</span>
                        : <span className="rounded px-2 py-0.5 text-xs bg-slate-500/20 text-cockpit-text-2 border border-slate-500/30">Inactif</span>}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => openEditType(t)}
                        className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 px-3 py-1 text-xs font-medium text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
                      >
                        Modifier
                      </button>
                      {t.is_active && (
                        <button
                          type="button"
                          onClick={() => handleDeactivateType(t)}
                          disabled={saving}
                          className="rounded border border-red-500/35 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fonctions */}
      <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 p-6 backdrop-blur-sm">
        <h3 className="text-base font-bold text-cockpit-text mb-4">Fonctions référent</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Identifiant unique (clé fonction)</label>
            <input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="ex. Référent Formation"
              className={inputCls}
            />
            <p className="text-xs text-cockpit-muted mt-1">Ne pas changer après usage en base.</p>
          </div>
          <div>
            <label className={labelCls}>Nom affiché</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="ex. Référent Formation"
              className={inputCls}
            />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Type (obligatoire — créé ci-dessus)</label>
            <select
              value={newFunctionTypeId === '' ? '' : String(newFunctionTypeId)}
              onChange={(e) => setNewFunctionTypeId(e.target.value === '' ? '' : Number(e.target.value))}
              className={inputCls}
            >
              <option value="">— Choisir un type —</option>
              {activeTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name} ({t.type_key})</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Description (optionnel)</label>
            <textarea
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              rows={2}
              className={`${inputCls} resize-none`}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleCreate}
          disabled={saving || !newKey.trim() || !newName.trim() || newFunctionTypeId === '' || activeTypes.length === 0}
          className="mt-4 rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Enregistrement…' : 'Créer la fonction'}
        </button>
      </div>

      <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 p-6 backdrop-blur-sm">
        <h3 className="text-base font-bold text-cockpit-text mb-4">Liste des fonctions</h3>
        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : definitions.length === 0 ? (
          <p className="text-cockpit-text-2 text-sm">Aucune définition.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1a2537] text-left">
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Clé</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Type</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Statut</th>
                  <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {definitions.map((d) => (
                  <tr key={d.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-cockpit-text">{d.function_key}</td>
                    <td className="px-4 py-3 text-cockpit-text">{d.name}</td>
                    <td className="px-4 py-3">
                      <span className="text-cockpit-text">{d.type_name}</span>
                      <span className="text-cockpit-muted text-xs ml-1">({d.type_key})</span>
                    </td>
                    <td className="px-4 py-3">
                      {d.is_active
                        ? <span className="rounded px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">Actif</span>
                        : <span className="rounded px-2 py-0.5 text-xs bg-slate-500/20 text-cockpit-text-2 border border-slate-500/30">Inactif</span>}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={() => openEdit(d)}
                        className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 px-3 py-1 text-xs font-medium text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
                      >
                        Modifier
                      </button>
                      {d.is_active && (
                        <button
                          type="button"
                          onClick={() => handleDeactivate(d)}
                          disabled={saving}
                          className="rounded border border-red-500/35 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingType && (
        <div className="fixed inset-0 bg-cockpit-bg-2/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="rounded-xl border border-[#223046]/90 bg-[#060a11] shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-base font-bold text-cockpit-text mb-4">Modifier le type — {editingType.type_key}</h3>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Clé du type</label>
                <input
                  value={editTypeKey}
                  onChange={(e) => setEditTypeKey(e.target.value.slice(0, TYPE_KEY_MAX))}
                  maxLength={TYPE_KEY_MAX}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Nom affiché</label>
                <input
                  value={editTypeName}
                  onChange={(e) => setEditTypeName(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea
                  value={editTypeDesc}
                  onChange={(e) => setEditTypeDesc(e.target.value)}
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
              </div>
              <label className="flex items-center gap-2 text-cockpit-text-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editTypeActive} onChange={(e) => setEditTypeActive(e.target.checked)} className="w-4 h-4 rounded" />
                Actif
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleSaveEditType}
                disabled={saving || !editTypeKey.trim() || !editTypeName.trim()}
                className="flex-1 rounded-lg border border-emerald-500/40 bg-emerald-500/20 py-2 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/30 disabled:opacity-50 transition-colors"
              >
                Enregistrer
              </button>
              <button
                type="button"
                onClick={() => setEditingType(null)}
                className="flex-1 rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 py-2 text-sm font-semibold text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-cockpit-bg-2/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="rounded-xl border border-[#223046]/90 bg-[#060a11] shadow-2xl max-w-lg w-full p-6">
            <h3 className="text-base font-bold text-cockpit-text mb-1">Modifier — {editing.function_key}</h3>
            <p className="text-cockpit-muted text-xs mb-4">La clé technique n'est pas modifiable.</p>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Nom affiché</label>
                <input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Type</label>
                <select
                  value={editFunctionTypeId === '' ? '' : String(editFunctionTypeId)}
                  onChange={(e) => setEditFunctionTypeId(e.target.value === '' ? '' : Number(e.target.value))}
                  className={inputCls}
                >
                  <option value="">— Choisir —</option>
                  {types.map((t) => (
                    <option key={t.id} value={t.id} disabled={!t.is_active && t.id !== editing.referent_type_id}>
                      {t.name} ({t.type_key}){!t.is_active ? ' — inactif' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={2}
                  className={`${inputCls} resize-none`}
                />
              </div>
              <label className="flex items-center gap-2 text-cockpit-text-2 text-sm cursor-pointer">
                <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} className="w-4 h-4 rounded" />
                Actif (proposé dans les listes)
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={saving || !editName.trim() || editFunctionTypeId === ''}
                className="flex-1 rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 disabled:opacity-50 transition-colors"
              >
                Enregistrer
              </button>
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 py-2 text-sm font-semibold text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferentDefinitionsContent;
